import React, { useState } from 'react';
import { Modal } from '../../admin/Modal';
import { Users } from 'lucide-react';

interface EditClientModeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentMode: 'self-managed' | 'coached';
  onSave: (newMode: 'self-managed' | 'coached') => void;
  onManageCoach?: () => void;
  assignedCoachName?: string;
}

export const EditClientModeModal: React.FC<EditClientModeModalProps> = ({
  isOpen,
  onClose,
  currentMode,
  onSave,
  onManageCoach,
  assignedCoachName
}) => {
  const [selectedMode, setSelectedMode] = useState<'self-managed' | 'coached'>(currentMode);

  const handleSave = () => {
    onSave(selectedMode);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change Coaching Status"
      variant="info"
      size="small"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save
          </button>
        </>
      }
    >
      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Current status: <strong>{currentMode === 'self-managed' ? 'Self-Managed' : 'Coached'}</strong>
        </p>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          padding: '14px',
          border: '2px solid var(--border)',
          borderRadius: '8px',
          cursor: currentMode === 'self-managed' ? 'not-allowed' : 'pointer',
          marginBottom: '12px',
          backgroundColor: selectedMode === 'self-managed' ? 'var(--primary-light)' : 'transparent',
          borderColor: selectedMode === 'self-managed' ? 'var(--primary)' : 'var(--border)',
          transition: 'all 0.2s ease',
          boxShadow: selectedMode === 'self-managed' ? '0 0 0 3px rgba(37, 99, 235, 0.1)' : 'none',
          opacity: currentMode === 'self-managed' ? 0.5 : 1
        }}>
          <input
            type="radio"
            name="client-mode"
            value="self-managed"
            checked={selectedMode === 'self-managed'}
            onChange={() => setSelectedMode('self-managed')}
            disabled={currentMode === 'self-managed'}
            style={{
              marginRight: '12px',
              marginTop: '3px',
              width: '18px',
              height: '18px',
              cursor: currentMode === 'self-managed' ? 'not-allowed' : 'pointer',
              accentColor: 'var(--primary)'
            }}
          />
          <div>
            <strong style={{
              display: 'block',
              marginBottom: '4px',
              color: currentMode === 'self-managed' ? 'var(--text-secondary)' : (selectedMode === 'self-managed' ? 'var(--primary)' : 'var(--text-primary)'),
              fontSize: '15px'
            }}>Self-Managed {currentMode === 'self-managed' && '(Current)'}</strong>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Client has full control of goals and tracking
            </span>
          </div>
        </label>

        <div style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: '12px'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            padding: '14px',
            border: '2px solid var(--border)',
            borderRadius: '8px',
            cursor: currentMode === 'coached' ? 'not-allowed' : 'pointer',
            backgroundColor: selectedMode === 'coached' ? 'var(--primary-light)' : 'transparent',
            borderColor: selectedMode === 'coached' ? 'var(--primary)' : 'var(--border)',
            transition: 'all 0.2s ease',
            boxShadow: selectedMode === 'coached' ? '0 0 0 3px rgba(37, 99, 235, 0.1)' : 'none',
            opacity: currentMode === 'coached' ? 0.5 : 1,
            flex: 1
          }}>
            <input
              type="radio"
              name="client-mode"
              value="coached"
              checked={selectedMode === 'coached'}
              onChange={() => setSelectedMode('coached')}
              disabled={currentMode === 'coached'}
              style={{
                marginRight: '12px',
                marginTop: '3px',
                width: '18px',
                height: '18px',
                cursor: currentMode === 'coached' ? 'not-allowed' : 'pointer',
                accentColor: 'var(--primary)'
              }}
            />
            <div>
              <strong style={{
                display: 'block',
                marginBottom: '4px',
                color: currentMode === 'coached' ? 'var(--text-secondary)' : (selectedMode === 'coached' ? 'var(--primary)' : 'var(--text-primary)'),
                fontSize: '15px'
              }}>Coached {currentMode === 'coached' && '(Current)'}</strong>
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                Coach manages goals for client
              </span>
            </div>
          </label>

          {/* Manage Coach Button - Only visible when in Coached mode */}
          {currentMode === 'coached' && onManageCoach && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onManageCoach();
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 16px',
                border: '2px solid var(--border)',
                borderRadius: '8px',
                backgroundColor: 'var(--bg-secondary)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '100px',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent-green)';
                e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
              }}
            >
              <Users size={20} style={{ color: 'var(--accent-green)' }} />
              <span style={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'var(--text-primary)',
                textAlign: 'center'
              }}>
                {assignedCoachName ? 'Change Coach' : 'Assign Coach'}
              </span>
              {assignedCoachName && (
                <span style={{
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  textAlign: 'center',
                  maxWidth: '80px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {assignedCoachName}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
